import React, { useState } from "react";
import PropTypes from "prop-types";
import { Alert, Box, CircularProgress, FormControlLabel, Switch, TextField } from "@mui/material";
import MDButton from "components/MDButton";
import api from "services/api";
import { useLanguage } from "context/LanguageContext";
import { useSnackbar } from "context/SnackbarContext";
import SettingsSection from "./components/SettingsSection";
import SaveBar from "./components/SaveBar";
import useCompanyForm from "./useCompanyForm";

const FIELDS = {
  inbox_email: "",
  invoice_allowed_extensions: ".pdf,.png,.jpg,.jpeg",
  invoice_sender_whitelist: "",
  invoice_email_pdf_split_enabled: false,
};

function DocumentReceiptTab({ companyId }) {
  const { t } = useLanguage();
  const showSnackbar = useSnackbar();
  const { values, dirty, saving, setField, save } = useCompanyForm(companyId, FIELDS);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  if (!values) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  const scanInbox = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const response = await api.post(`/api/company/${companyId}/scan-invoice-email/`, {
        limit: 100,
      });
      setScanResult(response.data);
    } catch (error) {
      showSnackbar(error.response?.data?.error || t("invoiceEmailScanFailed"), "error");
    } finally {
      setScanning(false);
    }
  };

  return (
    <>
      <SettingsSection title={t("stInbox")} description={t("stInboxHint")}>
        <TextField
          label={t("invoiceEmail")}
          type="email"
          value={values.inbox_email}
          onChange={setField("inbox_email")}
          helperText={t("stInboxEmailHint")}
          fullWidth
        />
        <Box>
          <MDButton
            variant="outlined"
            color="info"
            onClick={scanInbox}
            disabled={scanning || dirty || !values.inbox_email}
          >
            {scanning ? <CircularProgress size={18} color="inherit" /> : t("stCheckInbox")}
          </MDButton>
        </Box>
        {scanResult && (
          <Alert severity="success" onClose={() => setScanResult(null)}>
            {t("foundMessages")}: {scanResult.found_messages} · {t("importedDocuments")}:{" "}
            {scanResult.imported_documents} · {t("skippedMessages")}: {scanResult.skipped_messages}{" "}
            · {t("pendingSplitJobs")}: {scanResult.pending_split_jobs || 0}
          </Alert>
        )}
      </SettingsSection>

      <SettingsSection title={t("stFormats")}>
        <TextField
          label={t("invoiceAllowedExtensions")}
          value={values.invoice_allowed_extensions}
          onChange={setField("invoice_allowed_extensions")}
          helperText={t("stExtensionsHint")}
          fullWidth
        />
      </SettingsSection>

      <SettingsSection title={t("stSenders")} description={t("stSendersHint")}>
        <TextField
          label={t("invoiceSenderWhitelist")}
          value={values.invoice_sender_whitelist}
          onChange={setField("invoice_sender_whitelist")}
          multiline
          minRows={3}
          fullWidth
        />
      </SettingsSection>

      <SettingsSection title={t("stSplitTitle")} description={t("stSplitHint")} last>
        <FormControlLabel
          control={
            <Switch
              checked={!!values.invoice_email_pdf_split_enabled}
              onChange={setField("invoice_email_pdf_split_enabled")}
            />
          }
          label={t("invoiceEmailPdfSplitEnabled")}
        />
      </SettingsSection>

      <SaveBar dirty={dirty} saving={saving} onSave={save} />
    </>
  );
}

DocumentReceiptTab.propTypes = {
  companyId: PropTypes.number.isRequired,
};

export default DocumentReceiptTab;
